import { Point } from '@/types/common';

export interface Wall {
  index: number; // 0-based index in the polygon
  startPoint: Point;
  endPoint: Point;
  length: number; // in pixels
  angle: number; // angle in degrees
  midpoint: Point;
  identifier: string; // e.g., 'north', 'south', 'wall-1', etc.
}

/**
 * Extract walls from a room polygon
 * @param points Array of points forming the room polygon
 * @returns Array of wall segments
 */
export function extractWallsFromPolygon(points: Point[]): Wall[] {
  if (points.length < 3) return [];
  
  const walls: Wall[] = [];
  
  for (let i = 0; i < points.length; i++) {
    const startPoint = points[i];
    const endPoint = points[(i + 1) % points.length];
    
    // Calculate wall length
    const dx = endPoint.x - startPoint.x;
    const dy = endPoint.y - startPoint.y;
    const length = Math.sqrt(dx * dx + dy * dy);
    
    // Calculate angle (in degrees, 0 = horizontal right, 90 = vertical up)
    const angle = Math.atan2(dy, dx) * (180 / Math.PI);
    
    // Calculate midpoint
    const midpoint = {
      x: (startPoint.x + endPoint.x) / 2,
      y: (startPoint.y + endPoint.y) / 2
    };
    
    // Determine wall identifier based on angle
    const identifier = getWallIdentifier(angle, i, points.length);
    
    walls.push({
      index: i,
      startPoint,
      endPoint,
      length,
      angle,
      midpoint,
      identifier
    });
  }
  
  return walls;
}

/**
 * Get wall identifier based on angle and position
 * @param angle Wall angle in degrees
 * @param index Wall index in polygon
 * @param totalWalls Total number of walls
 * @returns Wall identifier string
 */
function getWallIdentifier(angle: number, index: number, totalWalls: number): string {
  // Normalize angle to 0-360 range
  const normalizedAngle = ((angle % 360) + 360) % 360;
  
  // For simple rectangular rooms (4 walls), use cardinal directions
  if (totalWalls === 4) {
    // Assuming walls are roughly aligned with cardinal directions
    if (normalizedAngle >= 315 || normalizedAngle < 45) {
      return 'east';
    } else if (normalizedAngle >= 45 && normalizedAngle < 135) {
      return 'north';
    } else if (normalizedAngle >= 135 && normalizedAngle < 225) {
      return 'west';
    } else {
      return 'south';
    }
  }
  
  // For complex polygons, use numbered identifiers
  return `wall-${index + 1}`;
}

/**
 * Find the closest wall to a point
 * @param point Click/hover point
 * @param walls Array of walls
 * @param threshold Maximum distance to consider (in pixels)
 * @returns The closest wall or null if none within threshold
 */
export function findClosestWall(point: Point, walls: Wall[], threshold: number = 20): Wall | null {
  let closestWall: Wall | null = null;
  let minDistance = threshold;
  
  for (const wall of walls) {
    const distance = pointToLineDistance(point, wall.startPoint, wall.endPoint);
    if (distance < minDistance) {
      minDistance = distance;
      closestWall = wall;
    }
  }
  
  return closestWall;
}

/**
 * Calculate distance from a point to a line segment
 * @param point The point
 * @param lineStart Start of the line segment
 * @param lineEnd End of the line segment
 * @returns Distance in pixels
 */
function pointToLineDistance(point: Point, lineStart: Point, lineEnd: Point): number {
  const dx = lineEnd.x - lineStart.x;
  const dy = lineEnd.y - lineStart.y;
  const lengthSquared = dx * dx + dy * dy;
  
  if (lengthSquared === 0) {
    // Line start and end are the same point
    return Math.sqrt((point.x - lineStart.x) ** 2 + (point.y - lineStart.y) ** 2);
  }
  
  // Calculate parameter t that represents position along the line segment
  const t = Math.max(0, Math.min(1, 
    ((point.x - lineStart.x) * dx + (point.y - lineStart.y) * dy) / lengthSquared
  ));
  
  // Find the closest point on the line segment
  const closestX = lineStart.x + t * dx;
  const closestY = lineStart.y + t * dy;
  
  // Calculate distance
  return Math.sqrt((point.x - closestX) ** 2 + (point.y - closestY) ** 2);
}

/**
 * Check if a point is near a wall segment
 * @param point The point to check
 * @param wall The wall segment
 * @param threshold Distance threshold in pixels
 * @returns True if point is within threshold distance of the wall
 */
export function isPointNearWall(point: Point, wall: Wall, threshold: number = 20): boolean {
  const distance = pointToLineDistance(point, wall.startPoint, wall.endPoint);
  return distance <= threshold;
}

/**
 * Calculate the real-world length of a wall using scale data
 * @param wall The wall segment
 * @param scaleData Scale data from floor plan
 * @returns Wall length in real-world units (meters/feet)
 */
export function calculateWallRealLength(wall: Wall, scaleData: any): number {
  if (!scaleData) return wall.length;
  
  const pixelsPerUnit = scaleData.pixelsPerUnit || 100;
  const unit = scaleData.unit || 'meters';
  
  // Convert pixel length to real-world units
  const realLength = wall.length / pixelsPerUnit;
  
  // If scale is in feet but we want meters, convert
  if (unit === 'feet' && scaleData.displayUnit === 'meters') {
    return realLength * 0.3048; // feet to meters
  }
  
  return realLength;
}

/**
 * Get wall orientation (horizontal, vertical, or diagonal)
 * @param wall The wall segment
 * @returns 'horizontal' | 'vertical' | 'diagonal'
 */
export function getWallOrientation(wall: Wall): 'horizontal' | 'vertical' | 'diagonal' {
  const angleThreshold = 15; // degrees of tolerance
  const normalizedAngle = Math.abs(wall.angle % 180);
  
  if (normalizedAngle < angleThreshold || normalizedAngle > 180 - angleThreshold) {
    return 'horizontal';
  } else if (Math.abs(normalizedAngle - 90) < angleThreshold) {
    return 'vertical';
  } else {
    return 'diagonal';
  }
}

/**
 * Generate a display name for a wall
 * @param wall The wall segment
 * @param roomName Optional room name
 * @returns Display name for the wall
 */
export function generateWallDisplayName(wall: Wall, roomName?: string): string {
  const prefix = roomName ? `${roomName} - ` : '';
  
  // For cardinal directions, capitalize the first letter
  if (['north', 'south', 'east', 'west'].includes(wall.identifier)) {
    return `${prefix}${wall.identifier.charAt(0).toUpperCase() + wall.identifier.slice(1)} Wall`;
  }
  
  // For numbered walls
  return `${prefix}Wall ${wall.index + 1}`;
}